/*
 |--------------------------------------------------------------------------
 | Currency
 |
 | This module adds functionality to the Sitecore Forms Currency component
 |--------------------------------------------------------------------------
 */

// Constants
const ACurrencySelector = 'input[data-input-type-currency="true"]';
const ACurrencyLabelSelector = '.a-input';
const ACurrencyInvalidClass = 'a-input--invalid';
const ACurrencyErrorMessageSelector = '.a-caption--error';
const ACurrencyHiddenAttribute = 'hidden';

// Class
class ACurrency {
  touched = false;
  euroSign = false;
  numbers = 9;
  digits = 2;

  constructor(element) {
    this.element = element;
    this.euroSign = this.element.dataset.currencySign === 'true';
    this.numbers = Number(this.element.dataset.maxNumbers) ?? this.numbers;
    this.digits = Number(this.element.dataset.digits) ?? this.digits;
  }

  init = () => {
    this.element.addEventListener('input', () => this.onInput());
    this.element.addEventListener('blur', () => this.onBlur());
    if (this.element.value && this.element.value !== '') {
      this.validate();
    }
  };

  onInput = () => {
    if (this.touched) {
      this.validate();
    }
  };

  onBlur = () => {
    const value = this.element.value;

    if (!value) return;

    if (value && !this.touched && value !== '') {
      this.touched = true;
    }

    this.format();
    this.validate();
  };

  validate = () => {
    const value = this.element.value;
    if (!value) {
      this.setFieldValidity(true);
      return;
    }

    const unformatted = this.unFormatDutchCurrency(value)?.toString();

    if (!unformatted || !unformatted.match(this.pattern())) {
      this.setFieldValidity(false);
    } else {
      this.setFieldValidity(true);
    }
  };

  format = () => {
    const value = this.element.value;
    const unformatted = this.unFormatDutchCurrency(value);

    if (!unformatted) {
      this.validate();
      return;
    }

    const formattedValue = this.formatDutchCurrency(value);
    if (formattedValue !== this.element.value) {
      this.element.value = formattedValue;
    }
  };

  formatDutchCurrency = (value) => {
    const unformatted = this.unFormatDutchCurrency(value);
    const formatted = new Intl.NumberFormat('nl', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: this.digits,
      maximumFractionDigits: this.digits,
      roundingMode: 'trunc',
    }).format(unformatted);

    return !this.euroSign ? formatted.replace(/[€\s]/g, '') : formatted;
  };

  unFormatDutchCurrency = (value) => {
    let result = null;
    if (value) {
      const valueAsString = value.toString();
      let unFormattedValue = valueAsString.replace(/[\.€\s]/g, '');
      unFormattedValue = unFormattedValue.replace(/,/g, '.');
      result = this.coerceNumberProperty(unFormattedValue, null);
    }
    return result;
  };

  coerceNumberProperty(value, fallbackValue = 0) {
    if (!isNaN(parseFloat(value)) && !isNaN(Number(value))) {
      return Number(value);
    }
    return arguments.length === 2 ? fallbackValue : 0;
  }

  setFieldValidity = (valid) => {
    const label = this.element.closest(ACurrencyLabelSelector);
    const errorMessage = label?.parentNode.querySelector(
      ACurrencyErrorMessageSelector
    );
    if (valid) {
      label.classList.remove(ACurrencyInvalidClass);
      this.element.setAttribute('aria-invalid', false);
      if (errorMessage) this.hide(errorMessage);
    } else {
      label.classList.add(ACurrencyInvalidClass);
      this.element.setAttribute('aria-invalid', true);
      if (errorMessage) this.show(errorMessage);
    }
  };

  pattern = () => {
    // optional minus, 1-9 numbers, optional decimal, 1-2 decimal characters
    return `^${this.euroSign ? '€?' : ''}-?\\d{1,${this.numbers}}(\\.\\d{1,2})?$`;
  };

  hide = (element) => {
    if (!element.hasAttribute(ACurrencyHiddenAttribute)) {
      element.setAttribute(ACurrencyHiddenAttribute, '');
    }
  };

  show = (element) => {
    element.removeAttribute(ACurrencyHiddenAttribute);
  };
}

// Initiate module
function ACurrencyCreateInstance(element) {
  if (!element.currencyClass) {
    element.currencyClass = new ACurrency(element);
    element.currencyClass.init();
  }
}

// Create new instances on DOM mutation
document
  .querySelectorAll(`${ACurrencySelector}`)
  .forEach(ACurrencyCreateInstance);

const ACurrencyOberver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${ACurrencySelector}`)
        .forEach(ACurrencyCreateInstance);
    });
  });
});

ACurrencyOberver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
